import { Link } from "react-router-dom";

import * as LatLngType      from 'shared/types/LatLng';
import * as DurationType    from 'shared/types/Duration';
import * as AgencyType      from 'shared/types/Agency';
import dayjs                from 'shared/utils/day-timezone';

import tectransit           from 'utils/TecTransit';
import * as googleMapsLinks from 'utils/googleMapsLinks';
import getOrderDescription  from 'utils/getOrderDescription';
import deletedOrderStyle    from 'utils/deletedOrderStyle';

import * as SchedulableType from 'Dispatcher/utils/Schedulable';
import WhiteboardContext    from 'Dispatcher/utils/WhiteboardContext';
import OnDemandDraftVehicleManifest from 'Dispatcher/utils/OnDemandDraftVehicleManifest';
import {
    DraggableSchedulableCell,
    DraggableUserSpan,
    DroppableSchedulableTable
}                           from "./DraggableDroppableWrapper";

export class OnDemandWhiteboardContext extends WhiteboardContext<SchedulableType.OrderSchedulable> {

    static DraftVehicleManifestConstructor  = OnDemandDraftVehicleManifest;

    getSchedulablesById() {
        return (this.agencyManifest.ordersById||{}) as Record<string,SchedulableType.OrderSchedulable>;
    }

    getHeaderText() : JSX.Element {
        return (<>
            During <b>{dayjs(this.agencyManifest.dayStartAt).tz(tectransit.agency.time_zone).format('MM/DD/YYYY')}</b>&nbsp;
            the agency has <b>{this.agencyManifest.vehicleManifests.length||'no'}</b> vehicle manifests&nbsp;
            and <b>{Object.keys(this.agencyManifest.ordersById||{}).length||'no'}</b> pre-scheduled orders.
        </>);
    }
    onDropOnSchedulablesTable( draggable:Record<string,any>, schedulable:SchedulableType.OrderSchedulable ) : Record<string,OnDemandDraftVehicleManifest> {
        // Moving order from a vehicle back to the order list
        if( !schedulable?.vehicle )
            throw Error(`This order is not allocated to a vehicle`);
        if( draggable.vehicle_id!==schedulable.vehicle._id )
            throw Error(`Draggable vehicle id is '${draggable.vehicle_id}' is not equal to order vehicle id '${schedulable.vehicle._id}'`);
        const srcVehicleManifest = this.draftVehicleManifestsById[draggable.vehicle_id] as OnDemandDraftVehicleManifest;
        if( !srcVehicleManifest )
            throw Error(`Cannot find vehicle manifest for vehicle id '${draggable.vehicle_id}'`);
        return {
            [draggable.vehicle_id] : srcVehicleManifest.getWithoutSchedulable(this,schedulable)
        };
    }
    onDropOnVehicle( vehicle_id:number, draggable:Record<string,any>, schedulable:SchedulableType.OrderSchedulable ) : Record<string,OnDemandDraftVehicleManifest> {
        if( typeof vehicle_id!=='number' )
            throw Error(`vehicle_id is not a number`);
        const dstVehicleManifest = this.draftVehicleManifestsById[vehicle_id] as OnDemandDraftVehicleManifest;
        if( !dstVehicleManifest )
            throw Error(`Cannot find vehicle manifest for vehicle id '${vehicle_id}'`);
        if( draggable.source==="schedulablesTable" ) {
            // Moving order from the order list down to a vehicle
            if( schedulable.vehicle )
                throw Error(`This order is already allocated to vehicle '${schedulable.vehicle.license_plate||'??'}, ${schedulable.vehicle.license_state||'??'}'`);
            return {
                [vehicle_id] : dstVehicleManifest.getWithSchedulable(this,schedulable)
            };
        }
        else if( draggable.source==="vehicleManifest" ) {
            // Moving order from one vehicle to another
            const srcVehicleManifest = this.draftVehicleManifestsById[draggable.vehicle_id] as OnDemandDraftVehicleManifest;
            if( !srcVehicleManifest )
                throw Error(`Cannot find vehicle manifest for vehicle id #${draggable.vehicle_id}`);
            if( srcVehicleManifest===dstVehicleManifest )
                throw Error(`Source and destination vehicles are the same`);
            // This means that we are dragging entire orders (because if we move 'pickup' from vehicle X to vehicle X,
            // then 'dropoff' also needs to be movedto Y. Because pickup and dropoff needs to be on the same vehicle.
            return {
                [draggable.vehicle_id]  : srcVehicleManifest.getWithoutSchedulable(this,schedulable),
                [vehicle_id]            : dstVehicleManifest.getWithSchedulable(this,schedulable)
            };
        }
        throw Error(`Unknown draggable source '${draggable.source}'`);
    }
    getSchedulablesTable(): JSX.Element {
        const schedulables = Object.values(this.getSchedulablesById()).sort(
            (so1, so2) => (so1.ordered_at || 0) - (so2.ordered_at || 0)
        );
        const columnNames = ["Vehicle", "Passenger", "Pickup", "Dropoff", "Type"];
        return (
            <table style={{ width: "100%" }}>
                <thead>
                    <tr>
                        {columnNames.map((name) => (
                            <th key={name} align="left">
                                {name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <DroppableSchedulableTable droppableArgs={{ id:`schedulablesTable`,data:(draggable:Record<string,any>,schedulable:SchedulableType.OrderSchedulable)=>{
                    return this.onDropOnSchedulablesTable(draggable,schedulable);
                } }}>
                    {schedulables.map((so) => {
                        const key = `${so._id!}@${so.vehicle ? so.vehicle._id : -1}`;
                        return (
                            <tr key={key} style={so.vehicle ? {} : { backgroundColor: "red" }}>
                                <td>
                                    {so.vehicle
                                        ? `${so.vehicle.license_plate || "??"}, ${
                                            so.vehicle?.license_state || "??"
                                        }`
                                        : "n/a"}
                                </td>
                                <DraggableSchedulableCell
                                    draggableArgs = {{
                                        id: `schedulable-${so._id}`,
                                        data: { 
                                            source      : "schedulablesTable",
                                            schedulable : so
                                        },
                                    }}
                                >
                                    {!so.user?._id ? (
                                        <span style={deletedOrderStyle}>deleted order</span>
                                    ) : (
                                        <Link to={`/Dispatcher/User/${so?.user_id!}`}>
                                            {so?.user?.name}
                                        </Link>
                                    )}
                                </DraggableSchedulableCell>
                                <td>
                                    {googleMapsLinks.getPlace(
                                        so.pickup.location,
                                        so.pickup.address
                                    )}
                                </td>
                                <td>
                                    {googleMapsLinks.getPlace(
                                        so.dropoff.location,
                                        so.dropoff.address
                                    )}
                                </td>
                                <td>
                                    <Link to={`/Dispatcher/StandingOrder/${so._id}`}>
                                        {getOrderDescription(
                                            so.type,
                                            so.repeat_pattern,
                                            dayjs(so.ordered_at || so.pickup.eta).tz(
                                                tectransit.agency.time_zone
                                            )
                                        )}
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                    <tr>
                        <th align="center" colSpan={columnNames.length}>
                            {schedulables.length} orders,{" "}
                            {schedulables.reduce(
                                (acc, so) => acc + (so.vehicle ? 0 : 1),
                                0
                            ) || "no"}{" "}
                            to be scheduled
                        </th>
                    </tr>
                </DroppableSchedulableTable>
            </table>
        );
    }
    getDraftVehicleManifestSteps(dvm: OnDemandDraftVehicleManifest): React.ReactNode {
        // Simply render all step cards sequentially, without DnD
        const stepCards = this.getDraftVehicleManifestStepCards(dvm);
        return <>{stepCards}</>;
    }
    getDraftVehicleManifestStepCards(dvm: OnDemandDraftVehicleManifest) : React.ReactNode[] {
        const tableStyle = {
            background      : (dvm.isIntact ? '#ffffff' : '#f0dd25'),
            borderRadius    : '0.5rem',
            boxShadow       : '0 4px 25px 0 rgb(0 0 0 / 10%)',
            padding         : '0.5rem',
            marginTop       : '5px',
            marginBottom    : '5px',
            width           : '100%'
        };
        let seatsTaken = 0;
        let wheelchairsTaken = 0;

        const getDriverLink = ( driverId:(number|undefined) ) => {
            const driver = this.driversById[driverId!];
            return (
                <Link 
                    key={`driver_link_for_${driver?._id??Date.now()}`}
                    title={driver?.name||`Driver #${driverId}`} 
                    to={`/Dispatcher/User/${driverId}`}
                >
                    {driver?.name||'??'}
                </Link>
            );
        };

        const scheduleByVehicleId = AgencyType.rehashSchedule(
            tectransit.agency.schedule_by_driver_id,
            (driverId,vehicleId) => vehicleId,
            (driverId,vehicleId) => driverId
        );
        const vehicleId = dvm.originalVm.vehicle_id;
        const driverScheduleByWeekday = scheduleByVehicleId[vehicleId]||{};

        return dvm.steps.map((step,stepNdx) => {
            const pickups  = step.pickups || [];
            const dropoffs = step.dropoffs || [];
            const showStepName = (step.name!=='stop') || ((pickups.length+dropoffs.length)===0);
            const secondsStyle = step.problem ? {color:'red',fontWeight:'bold'} : {};
            const stepMoment = step.seconds ? dayjs(step.seconds*1000).tz(tectransit.agency.time_zone) : undefined;
            const stepRanges = stepMoment ? driverScheduleByWeekday[stepMoment.format('dddd').toLowerCase()] : undefined;
            const stepMinutes = stepMoment ? stepMoment.diff(stepMoment.startOf('day'),'minutes') : undefined;
            const stepRange = stepRanges?.find(sr => (sr[0]<=stepMinutes!) && (stepMinutes!<sr[1]));
            return (
                <table key={`${stepNdx}_${step.name}`} style={tableStyle}>
                    <tbody>
                        {step.latlng ? (
                            <tr>
                                <td style={{maxWidth:'30px',overflow:'hidden',whiteSpace:'nowrap',verticalAlign:'top'}}>
                                    {getDriverLink(stepRange?.[2])}
                                </td>
                                <td colSpan={2} valign="top">
                                    {googleMapsLinks.getPlace(step.latlng, step.address, googleMapsLinks.onlyFirstLineAddressFormatter)}
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td colSpan={3}>{getDriverLink(stepRange?.[2])}</td>
                            </tr>
                        )}
                        <tr>
                            <td style={{width:'10%',verticalAlign:'top',...secondsStyle}} title={step.problem||stepMoment?.format(tectransit.timeFormat)||''}>
                                {stepMoment?.format('HH:mm')||'??'}
                            </td>
                            <td style={{width:'65%',verticalAlign:'top'}}>
                                {showStepName ? <strong title={`Taken seats:${seatsTaken}, wheelchairs:${wheelchairsTaken}`}>{step.name}</strong> : ''}
                                {(dropoffs.length>0) && (<div>
                                    <strong title={`Before dropoff: seats:${seatsTaken}, wheelchairs:${wheelchairsTaken}`}>dropoff</strong>
                                    {dropoffs.map(so => {
                                        seatsTaken       -= so.seats;
                                        wheelchairsTaken -= so.wheelchairs;
                                        return !so.user?._id ? <span style={deletedOrderStyle}>deleted order</span> : (
                                        <DraggableUserSpan
                                            key             = {so._id}
                                            draggableArgs   = {{
                                                id  : `vehicle_${dvm.originalVm.vehicle_id}_${so.type.split('_')[0]}_${stepNdx}_0_${so._id}`,
                                                data: { 
                                                    source      : `vehicleManifest`,
                                                    vehicle_id  : dvm.originalVm.vehicle_id,
                                                    schedulable : so
                                                }
                                            }}
                                        >
                                            {<Link to={`/Dispatcher/StandingOrder/${so._id}`}>
                                                {so?.user?.name}
                                            </Link>}
                                        </DraggableUserSpan>);
                                    })}
                                </div>)}
                                {(pickups.length>0) && (<div>
                                    <strong title={`Before pickup: seats:${seatsTaken}, wheelchairs:${wheelchairsTaken}`}>pickup</strong>
                                    {pickups.map((so) => {
                                        seatsTaken += so.seats;
                                        wheelchairsTaken += so.wheelchairs;
                                        return !so.user?._id ? (<span style={deletedOrderStyle}>deleted order</span>) : (
                                            <DraggableUserSpan
                                                key            = {so._id}
                                                draggableArgs  = {{
                                                    id  : `vehicle_${dvm.originalVm.vehicle_id}_${so.type.split('_')[0]}_${stepNdx}_1_${so._id}`,
                                                    data: {
                                                        source      : `vehicleManifest`,
                                                        vehicle_id  : dvm.originalVm.vehicle_id,
                                                        schedulable : so
                                                    }
                                                }}
                                            >
                                                {<Link to={`/Dispatcher/StandingOrder/${so._id}`}>
                                                    {so?.user?.name}
                                                </Link>}
                                            </DraggableUserSpan>
                                        );
                                    })}
                                </div>)}
                            </td>
                            <td style={{width:'100px'}}>
                                { step.duration ? DurationType.secondsToHMS(step.duration,60) : '??' }
                            </td>
                        </tr>
                    </tbody>
                </table>
            );
        });
    }

    getDraftVehicleManifestStops(dvm:OnDemandDraftVehicleManifest) : JSX.Element {
        const latlngs = dvm.steps.filter(s=>!!s.latlng).map(s=>s.latlng!);
        return (
            <a href={`https://www.google.com/maps/dir/${latlngs.map(ll=>encodeURI(LatLngType.toString(ll))).join('/')}`}>
                {`${(!dvm.isIntact)?'about ':''}${latlngs.length}`}
            </a>
        );
    }
}

export default OnDemandWhiteboardContext;
